<!--
  PlatformDefaultTimezone -- SuperAdmin global-defaults editor for the
  platform-wide default timezone (the second layer of the 3-layer
  resolver chain that drives "today" for each distributor sidebar).

  Bug #115: the distributor Profile page rendered "Not set (falls back
  to UTC)" -- a placeholder, not the product contract. The actual
  fallback chain is::

      distributor.timezone
      ?? platform_settings['default_timezone']
      ?? 'UTC'   (final hardcoded safety net)

  This component is the SA surface for the second layer. Lives on
  /admin/plans-pricing (PlansPricing.vue), positioned below the
  margin sections. Carries its own local "Live" indicator alongside
  the sibling platform-settings widgets so reviewers can see at a
  glance that this card is real even when the rest of the page is
  mock plans data.

  Endpoint:
    * GET   /api/platform-settings/default-timezone
        -> {"value": <IANA name or null>}
        @login_required only (non-sensitive informational value)
    * PATCH /api/platform-settings/default-timezone
        body {"value": <IANA name or null>}
        gated on ``superadmin.settings`` (existing high-watermark cap)

  data-testid prefix: platform-default-timezone-*. ASCII-only.
-->
<template>
    <section
        class="platform-default-timezone"
        data-testid="platform-default-timezone-root"
    >
        <div class="platform-default-timezone__header">
            <h2 class="platform-default-timezone__title">
                Platform default timezone
            </h2>
            <span
                class="platform-default-timezone__live-badge"
                data-testid="platform-default-timezone-live"
                title="This section is backed by the live platform_settings endpoint -- not mock data."
            >
                Live
            </span>
        </div>

        <p class="platform-default-timezone__lede">
            Drives the "today" window for any distributor that has not
            set their own timezone. Clearing this field falls back to
            UTC (the final hardcoded safety net).
        </p>

        <v-progress-circular
            v-if="loading"
            :indeterminate="true"
            color="primary"
            size="32"
            width="4"
            class="mt-4"
            data-testid="platform-default-timezone-loading"
        />

        <div v-else class="platform-default-timezone__form">
            <label
                class="platform-default-timezone__label"
                for="platform-default-timezone-input"
            >
                Default timezone
            </label>
            <select
                id="platform-default-timezone-input"
                v-model="draft"
                class="platform-default-timezone__input"
                data-testid="platform-default-timezone-input"
                @change="onInput"
            >
                <option value="">-- Not set (falls back to UTC) --</option>
                <option
                    v-for="tz in timezones"
                    :key="tz.value"
                    :value="tz.value"
                >
                    {{ tz.label }}
                </option>
            </select>

            <p
                v-if="errorMsg"
                class="platform-default-timezone__error"
                data-testid="platform-default-timezone-error"
            >
                {{ errorMsg }}
            </p>
            <p
                v-else
                class="platform-default-timezone__hint"
                data-testid="platform-default-timezone-hint"
            >
                {{ hint }}
            </p>

            <div class="platform-default-timezone__actions">
                <button
                    type="button"
                    class="tf-btn tf-btn--primary"
                    :disabled="!canSave"
                    data-testid="platform-default-timezone-save"
                    @click="save"
                >
                    {{ saving ? "Saving..." : "Save" }}
                </button>
                <button
                    v-if="hasStoredValue"
                    type="button"
                    class="tf-btn tf-btn--ghost"
                    :disabled="saving"
                    data-testid="platform-default-timezone-reset"
                    @click="resetToCleared"
                >
                    Clear default
                </button>
            </div>
        </div>
    </section>
</template>

<script>
import { computed, ref, onMounted } from "vue";
import axios from "@/axios";
import { COMMON_TIMEZONES } from "@/constants/timezones";

const URL = "/api/platform-settings/default-timezone";

export default {
    name: "PlatformDefaultTimezone",

    setup() {
        // Stored IANA name or null. ``draft`` mirrors the select's
        // current value; empty-string sentinel translates to null on
        // the save path.
        const stored = ref(null);
        const draft = ref("");
        const loading = ref(true);
        const saving = ref(false);
        const errorMsg = ref("");

        const timezones = COMMON_TIMEZONES;

        const hasStoredValue = computed(() => stored.value !== null);

        const hint = computed(() => {
            if (stored.value === null) {
                return "No platform default set. Distributors without their own timezone fall back to UTC.";
            }
            const match = COMMON_TIMEZONES.find(
                (tz) => tz.value === stored.value
            );
            const label = match ? `${match.label} (${stored.value})` : stored.value;
            return `Current platform default: ${label}.`;
        });

        const canSave = computed(() => {
            if (saving.value) return false;
            if (errorMsg.value) return false;
            // Empty draft is a "clear" -- only valid when something
            // is currently stored.
            if (draft.value === "") return stored.value !== null;
            return true;
        });

        async function load() {
            loading.value = true;
            errorMsg.value = "";
            try {
                const resp = await axios.get(URL);
                const value = resp?.data?.value ?? null;
                stored.value = typeof value === "string" ? value : null;
                draft.value = stored.value === null ? "" : stored.value;
            } catch (e) {
                errorMsg.value = "Could not load the platform default timezone.";
            } finally {
                loading.value = false;
            }
        }

        function onInput() {
            errorMsg.value = "";
        }

        async function save() {
            saving.value = true;
            errorMsg.value = "";
            const next = draft.value === "" ? null : draft.value;
            try {
                const resp = await axios.patch(URL, { value: next });
                const v = resp?.data?.value ?? null;
                stored.value = typeof v === "string" ? v : null;
                draft.value = stored.value === null ? "" : stored.value;
            } catch (e) {
                const status = e?.response?.status;
                if (status === 403) {
                    errorMsg.value =
                        "Permission denied. Only Super Admins can edit the platform default timezone.";
                } else if (status === 400) {
                    errorMsg.value =
                        "Invalid timezone. Pick a value from the list.";
                } else {
                    errorMsg.value =
                        "Could not save the platform default timezone.";
                }
            } finally {
                saving.value = false;
            }
        }

        async function resetToCleared() {
            draft.value = "";
            await save();
        }

        onMounted(load);

        return {
            loading,
            saving,
            errorMsg,
            hint,
            draft,
            stored,
            timezones,
            hasStoredValue,
            canSave,
            onInput,
            save,
            resetToCleared,
        };
    },
};
</script>

<style scoped>
.platform-default-timezone {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 16px 20px;
    margin-bottom: 24px;
}
.platform-default-timezone__header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
}
.platform-default-timezone__title {
    font-size: 18px;
    font-weight: 600;
    color: #1a202c;
    margin: 0;
}
.platform-default-timezone__live-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    background: #c6f6d5;
    color: #22543d;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.platform-default-timezone__lede {
    margin: 0 0 16px;
    color: #4b5563;
    max-width: 640px;
    line-height: 1.5;
    font-size: 14px;
}
.platform-default-timezone__form {
    max-width: 420px;
}
.platform-default-timezone__label {
    display: block;
    font-weight: 600;
    margin-bottom: 8px;
    color: #1f2937;
    font-size: 14px;
}
.platform-default-timezone__input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 1rem;
    background: #fff;
}
.platform-default-timezone__hint {
    margin-top: 8px;
    color: #4b5563;
    font-size: 0.875rem;
}
.platform-default-timezone__error {
    margin-top: 8px;
    color: #b91c1c;
    font-size: 0.875rem;
}
.platform-default-timezone__actions {
    margin-top: 16px;
    display: flex;
    gap: 8px;
}
.tf-btn {
    border: 1px solid transparent;
    border-radius: 6px;
    padding: 8px 20px;
    font-size: 1rem;
    cursor: pointer;
}
.tf-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}
.tf-btn--primary {
    background: #1f2937;
    color: #fff;
}
.tf-btn--ghost {
    background: transparent;
    color: #1f2937;
    border-color: #d1d5db;
}
</style>
